/**
 * Shared inventory item form — used by both admin and main inventory pages.
 * Includes: ItemForm (with Generate, collapsible sections, pills, tag input),
 * plus supporting components (Section, PillSelector, TagInput, useLoadingDots).
 */

import { useState, useMemo, useEffect, type KeyboardEvent } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ChevronDown, Sparkles, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import type { TFunction } from "@/i18n/catalog";
import type { Locale } from "@/i18n/config";
import { useTranslation } from "@/i18n/use-translation";
import { currencySymbol, formatCurrency, parseLocaleNumber } from "@/lib/format";
import { allergenLabel, EU_14_ALLERGENS } from "@/lib/allergens";
import { storageTypeLabel } from "@/lib/storage-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { apiError, showApiErrorAs } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

// ── Types ──────────────────────────────────────────────────────────────

export interface FoodCategory {
  id: string;
  name: string;
  slug?: string;
  parent_id: string | null;
  sort_order: number;
}

export interface StorageType {
  id: string;
  name: string;
  display_name: string;
  sort_order?: number;
}

export interface InventoryItemData {
  id?: string;
  description: string;
  sku: string | null;
  category_id: string | null;
  subcategory_id: string | null;
  storage_type_id: string | null;
  unit_pack_size: string | null;
  price: number | null;
  currency: string;
  price_per_kg: number | null;
  typical_shelf_life_days: number | null;
  typical_weight_kg: number | null;
  edible_yield_pct: number | null;
  allergens: string[];
  aliases: string[];
  supplier: string | null;
  status: string;
}

// ── Schema ─────────────────────────────────────────────────────────────

/**
 * Built from `t` and `locale` inside the component (POS2-1134 CP3b).
 *
 * Two reasons, and the second is the one that matters. A module-level schema captures `t` at
 * import, so its messages would stay in whichever language the tab booted in. And `z.coerce.number`
 * is `Number()`, which reads `"1,5"` as `NaN` — so a German kitchen typing one and a half kilos
 * either saw a validation error or, with `parseFloat`, silently logged one. The numeric fields
 * parse through `parseLocaleNumber` instead, which is why they are `type="text"
 * inputMode="decimal"` below rather than `type="number"`: a number input refuses a comma outright,
 * so no amount of parsing downstream would ever see it (pinned decision 3).
 */
export function makeSchema(t: TFunction, locale: Locale) {
  /** "" → undefined (the field is optional); anything else → the number the user meant. A string
   *  that is not a number stays a string, so Zod reports it rather than coercing it to NaN. */
  const parsed = (v: unknown) => {
    if (v === "" || v === null || v === undefined) return undefined;
    if (typeof v !== "string") return v;
    return parseLocaleNumber(locale, v) ?? v;
  };
  const optNum = z.preprocess(
    parsed,
    z
      .number({ invalid_type_error: t("stock.notANumber") })
      .positive(t("stock.mustBePositive"))
      .optional(),
  );
  const optInt = z.preprocess(
    parsed,
    z
      .number({ invalid_type_error: t("stock.notANumber") })
      .int(t("stock.wholeDaysOnly"))
      .positive(t("stock.mustBePositive"))
      .optional(),
  );
  const optPct = z.preprocess(
    parsed,
    z
      .number({ invalid_type_error: t("stock.notANumber") })
      .min(0, t("stock.percentRange"))
      .max(100, t("stock.percentRange"))
      .optional(),
  );

  return z.object({
    description: z.string().min(1, t("stock.itemDescriptionRequired")),
    sku: z.string().optional(),
    category_id: z.string().optional(),
    subcategory_id: z.string().optional(),
    storage_type_id: z.string().optional(),
    unit_pack_size: z.string().optional(),
    price: optNum,
    currency: z.string().default("EUR"),
    price_per_kg: optNum,
    typical_shelf_life_days: optInt,
    typical_weight_kg: optNum,
    edible_yield_pct: optPct,
    supplier: z.string().optional(),
    status: z
      .enum(["active", "inactive", "pending_review", "discontinued", "seasonal"])
      .default("active"),
  });
}

export type ItemFormData = z.infer<ReturnType<typeof makeSchema>>;

// ── Constants ──────────────────────────────────────────────────────────

// ── Helpers ────────────────────────────────────────────────────────────

function parseWeightFromUnit(unit: string): number | null {
  const m = unit.match(/^([\d.]+)\s*(kg|g|l|ml)/i);
  if (!m) return null;
  const val = parseFloat(m[1]);
  const u = m[2].toLowerCase();
  if (u === "kg" || u === "l") return val;
  if (u === "g" || u === "ml") return val / 1000;
  return null;
}

export function itemFormToBody(
  data: ItemFormData,
  allergens: string[],
  aliases: string[],
  cleanName?: string,
  simpleName?: string,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    description: data.description,
    status: data.status,
    currency: data.currency,
    allergens,
    aliases,
  };
  if (cleanName !== undefined) body.clean_name = cleanName || null;
  if (simpleName !== undefined) body.simple_name = simpleName || null;
  if (data.sku) body.sku = data.sku;
  if (data.category_id) body.category_id = data.category_id;
  if (data.subcategory_id) body.subcategory_id = data.subcategory_id;
  if (data.storage_type_id) body.storage_type_id = data.storage_type_id;
  if (data.unit_pack_size) body.unit_pack_size = data.unit_pack_size;
  if (data.price) body.price = Number(data.price);
  if (data.price_per_kg) body.price_per_kg = Number(data.price_per_kg);
  if (data.typical_shelf_life_days)
    body.typical_shelf_life_days = Number(data.typical_shelf_life_days);
  if (data.typical_weight_kg) body.typical_weight_kg = Number(data.typical_weight_kg);
  if (data.edible_yield_pct != null) body.edible_yield_pct = Number(data.edible_yield_pct);
  if (data.supplier) body.supplier = data.supplier;
  return body;
}

// ── Loading Dots ───────────────────────────────────────────────────────

function useLoadingDots(active: boolean): string {
  const [count, setCount] = useState(1);
  useEffect(() => {
    if (!active) {
      setCount(1);
      return;
    }
    const id = setInterval(() => setCount((c) => (c % 3) + 1), 400);
    return () => clearInterval(id);
  }, [active]);
  return ".".repeat(count);
}

// ── Pill Selector ──────────────────────────────────────────────────────

function PillSelector({
  options,
  value,
  onChange,
  id,
  multi,
  selected,
  onToggle,
}: {
  options: { id: string; label: string }[];
  value?: string;
  onChange?: (v: string) => void;
  id?: string;
  multi?: boolean;
  selected?: string[];
  onToggle?: (v: string) => void;
}) {
  return (
    <div id={id} className="flex flex-wrap gap-1.5" role={multi ? "group" : "radiogroup"}>
      {options.map((opt) => {
        const isActive = multi ? (selected ?? []).includes(opt.id) : value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role={multi ? "checkbox" : "radio"}
            aria-checked={isActive}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${isActive ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
            onClick={() => (multi ? onToggle?.(opt.id) : onChange?.(opt.id))}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Collapsible Section ────────────────────────────────────────────────

function Section({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center gap-1.5 py-1 text-xs font-medium uppercase tracking-wider text-gray-400 hover:text-gray-600"
        onClick={onToggle}
      >
        <ChevronDown className={`size-3 transition-transform ${open ? "" : "-rotate-90"}`} />
        {title}
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${open ? "mt-3 max-h-[2000px] opacity-100" : "max-h-0 opacity-0"}`}
      >
        <div className="space-y-3">{children}</div>
      </div>
    </div>
  );
}

// ── Tag Input ──────────────────────────────────────────────────────────

function TagInput({
  tags,
  onTagsChange,
  suggestions,
  placeholder,
  id,
}: {
  tags: string[];
  onTagsChange: (t: string[]) => void;
  suggestions?: readonly string[];
  placeholder?: string;
  id?: string;
}) {
  const [input, setInput] = useState("");
  const filtered = useMemo(() => {
    if (!suggestions || !input) return [];
    const q = input.toLowerCase();
    return suggestions.filter((s) => s.includes(q) && !tags.includes(s));
  }, [suggestions, input, tags]);

  function add(tag: string) {
    const t = tag.trim().toLowerCase();
    if (t && !tags.includes(t)) onTagsChange([...tags, t]);
    setInput("");
  }
  function remove(tag: string) {
    onTagsChange(tags.filter((t) => t !== tag));
  }
  function handleKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(input);
    }
    if (e.key === "Backspace" && !input && tags.length > 0) remove(tags[tags.length - 1]);
  }

  return (
    <div>
      <div className="flex min-h-9 flex-wrap items-center gap-1 rounded-md border border-gray-200 px-2 py-1.5 focus-within:border-gray-300 focus-within:ring-1 focus-within:ring-blue-500">
        {tags.map((tag) => (
          <Badge key={tag} variant="secondary" className="gap-0.5 rounded-full px-2 py-0 text-xs">
            {tag}
            <button
              type="button"
              onClick={() => remove(tag)}
              className="ml-0.5 text-gray-400 hover:text-red-500"
            >
              <X className="size-3" />
            </button>
          </Badge>
        ))}
        <input
          id={id}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder={tags.length === 0 ? placeholder : ""}
          className="min-w-[80px] flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400"
        />
      </div>
      {filtered.length > 0 && (
        <div className="mt-1 rounded-md border border-gray-200 bg-white p-1 shadow-sm">
          {filtered.map((s) => (
            <button
              key={s}
              type="button"
              className="block w-full rounded px-2 py-1 text-left text-xs text-gray-700 hover:bg-gray-100"
              onClick={() => add(s)}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Item Form ──────────────────────────────────────────────────────────

export function ItemForm({
  defaultValues,
  defaultAllergens,
  defaultAliases,
  defaultCleanName,
  defaultSimpleName,
  categories,
  subcategoriesFor,
  storageTypes,
  onSubmit,
  isPending,
  submitLabel,
  listId,
  onEditExisting,
  onDelete,
  isDeleting,
}: {
  defaultValues?: Partial<ItemFormData>;
  defaultAllergens?: string[];
  defaultAliases?: string[];
  defaultCleanName?: string;
  defaultSimpleName?: string;
  categories: FoodCategory[];
  subcategoriesFor: (parentId: string) => FoodCategory[];
  storageTypes: StorageType[];
  onSubmit: (
    data: ItemFormData,
    allergens: string[],
    aliases: string[],
    cleanName?: string,
    simpleName?: string,
  ) => void;
  isPending: boolean;
  submitLabel: string;
  listId?: string;
  onEditExisting?: (item: InventoryItemData) => void;
  onDelete?: () => void;
  isDeleting?: boolean;
}) {
  const headers = useApiHeaders();
  const [allergens, setAllergens] = useState<string[]>(defaultAllergens ?? []);
  const [aliases, setAliases] = useState<string[]>(defaultAliases ?? []);
  const [cleanName, setCleanName] = useState(defaultCleanName ?? "");
  const [simpleName, setSimpleName] = useState(defaultSimpleName ?? "");
  const [generating, setGenerating] = useState(false);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<InventoryItemData | null>(null);
  const [productOpen, setProductOpen] = useState(!!defaultValues);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [specsOpen, setSpecsOpen] = useState(false);
  const dots = useLoadingDots(generating);
  const { locale, t } = useTranslation();
  const schema = useMemo(() => makeSchema(t, locale), [t, locale]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ItemFormData>({
    resolver: zodResolver(schema),
    defaultValues: { currency: "EUR", status: "active", edible_yield_pct: 75, ...defaultValues },
  });

  const selectedCategoryId = watch("category_id");
  const subcategories = selectedCategoryId ? subcategoriesFor(selectedCategoryId) : [];
  const price = watch("price");
  // The item carries its own currency as a form field, so the two price labels read that
  // rather than the hardcoded `€` they held — which was wrong the moment the field was
  // changed, and for every non-euro organisation (POS2-1134). The key already takes it.
  const itemCurrency = currencySymbol(locale, watch("currency"));
  const unitStr = watch("unit_pack_size");

  const autoPricePerKg = useMemo(() => {
    if (!price || !unitStr) return null;
    const weight = parseWeightFromUnit(unitStr);
    if (!weight || weight <= 0) return null;
    return Math.round((price / weight) * 100) / 100;
  }, [price, unitStr]);

  async function runGenerate(desc: string) {
    setSuggestion(null);
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/inventory-items/generate`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          description: desc,
          unit_pack_size: getValues("unit_pack_size") || null,
          price: getValues("price") || null,
        }),
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      const { data } = await res.json();

      // Pre-fill SKU
      if (listId) {
        try {
          const listRes = await fetch(`${API_URL}/api/v1/inventory-lists`, { headers });
          if (listRes.ok) {
            const lists = (await listRes.json()).data;
            const currentList = lists.find((l: { id: string }) => l.id === listId);
            if (currentList) {
              const nextNum = currentList.item_count + 1;
              setValue("sku", `${currentList.list_code}${String(nextNum).padStart(5, "0")}`);
            }
          }
        } catch {
          /* non-critical */
        }
      }

      if (data.unit_pack_size) setValue("unit_pack_size", data.unit_pack_size);
      if (data.price) setValue("price", data.price);
      if (data.storage_type_id) setValue("storage_type_id", data.storage_type_id);
      if (data.price_per_kg) setValue("price_per_kg", data.price_per_kg);
      if (data.typical_shelf_life_days)
        setValue("typical_shelf_life_days", data.typical_shelf_life_days);
      if (data.typical_weight_kg) setValue("typical_weight_kg", data.typical_weight_kg);
      if (data.edible_yield_pct != null) setValue("edible_yield_pct", data.edible_yield_pct);
      if (data.allergens?.length) setAllergens(data.allergens);
      if (data.aliases?.length) setAliases(data.aliases);

      if (data.category_id) {
        setValue("category_id", data.category_id);
        if (data.subcategory_id) {
          requestAnimationFrame(() => setValue("subcategory_id", data.subcategory_id));
        }
      }

      setProductOpen(true);
      setPricingOpen(true);
      setSpecsOpen(true);
      toast.success(t("stock.generatedByAi"));
    } catch (e) {
      showApiErrorAs(t("stock.generateFailed"), e as Error);
    } finally {
      setGenerating(false);
    }
  }

  async function handleGenerate() {
    const desc = getValues("description");
    if (!desc?.trim()) {
      toast.error(t("stock.enterDescriptionFirst"));
      return;
    }
    setSuggestion(null);
    setDuplicate(null);

    // Duplicate check
    if (listId) {
      try {
        const params = new URLSearchParams({ search: desc, limit: "1" });
        const res = await fetch(`${API_URL}/api/v1/inventory-lists/${listId}/items?${params}`, {
          headers,
        });
        if (res.ok) {
          const { data } = await res.json();
          if (data?.length > 0) {
            const match = data[0];
            if (
              match.description.toLowerCase().includes(desc.toLowerCase()) ||
              desc.toLowerCase().includes(match.description.toLowerCase())
            ) {
              setDuplicate(match);
              return;
            }
          }
        }
      } catch {
        /* continue */
      }
    }

    // Food validation
    try {
      const res = await fetch(`${API_URL}/api/v1/inventory-items/validate-description`, {
        method: "POST",
        headers,
        body: JSON.stringify({ description: desc }),
      });
      if (res.ok) {
        const { data } = await res.json();
        if (!data.is_food) {
          setSuggestion(data.suggestion ?? null);
          return;
        }
      }
    } catch {
      /* continue */
    }

    await runGenerate(desc);
  }

  async function handleSuggestionClick() {
    if (!suggestion) return;
    setValue("description", suggestion);
    setSuggestion(null);
    await runGenerate(suggestion);
  }

  return (
    <form
      onSubmit={handleSubmit((data) => {
        if (autoPricePerKg && !data.price_per_kg) data.price_per_kg = autoPricePerKg;
        onSubmit(data, allergens, aliases, cleanName || undefined, simpleName || undefined);
      })}
      className="space-y-6"
    >
      {/* Description + Generate */}
      <div>
        <label className="mb-1 block text-xs text-gray-400" htmlFor="item-description">
          {t("stock.itemDescription")} <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-2">
          <Input
            id="item-description"
            className="flex-1 text-base"
            placeholder={t("stock.itemDescriptionPlaceholder")}
            {...register("description")}
          />
          <Button
            type="button"
            variant="outline"
            className="shrink-0 gap-1.5 px-3"
            disabled={generating}
            onClick={handleGenerate}
          >
            <Sparkles className={`size-4 ${generating ? "animate-pulse text-amber-500" : ""}`} />
            {generating ? t("stock.generating") : t("stock.generate")}
          </Button>
        </div>
        {errors.description && (
          <p className="mt-1 text-xs text-red-600">{errors.description.message}</p>
        )}
        {duplicate && (
          <p className="mt-1.5 text-xs text-gray-500">
            <span className="font-medium text-gray-700">{duplicate.description}</span>{" "}
            {t("stock.alreadyInInventory")}{" "}
            {onEditExisting && (
              <button
                type="button"
                className="font-medium text-blue-600 hover:underline"
                onClick={() => {
                  onEditExisting(duplicate);
                  setDuplicate(null);
                }}
              >
                {t("stock.editItInstead")}
              </button>
            )}
          </p>
        )}
        {suggestion && !duplicate && (
          <p className="mt-1.5 text-xs text-gray-500">
            {t("stock.didYouMean")}{" "}
            <button
              type="button"
              className="font-medium text-blue-600 hover:underline"
              onClick={handleSuggestionClick}
            >
              {suggestion}
            </button>
            ?
          </p>
        )}
      </div>

      {/* Clean Name (search name) */}
      <div>
        <label className="mb-1 block text-xs text-gray-400" htmlFor="item-clean-name">
          {t("stock.searchName")}{" "}
          <span className="text-xs text-gray-300">{t("stock.searchNameHint")}</span>
        </label>
        <Input
          id="item-clean-name"
          className="text-sm"
          placeholder={t("stock.searchNamePlaceholder")}
          value={cleanName}
          onChange={(e) => setCleanName(e.target.value)}
        />
      </div>

      {/* Simple Name (core ingredient) */}
      <div>
        <label className="mb-1 block text-xs text-gray-400" htmlFor="item-simple-name">
          {t("stock.simpleName")}{" "}
          <span className="text-xs text-gray-300">{t("stock.simpleNameHint")}</span>
        </label>
        <Input
          id="item-simple-name"
          className="text-sm"
          placeholder={t("stock.simpleNamePlaceholder")}
          value={simpleName}
          onChange={(e) => setSimpleName(e.target.value)}
        />
      </div>

      {/* Product Info */}
      <Section
        title={t("stock.sectionProduct")}
        open={productOpen}
        onToggle={() => setProductOpen(!productOpen)}
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-gray-400" htmlFor="item-sku">
              {t("stock.sku")}
            </label>
            <Input id="item-sku" placeholder={t("stock.skuPlaceholder")} {...register("sku")} />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-400" htmlFor="item-unit">
              {t("stock.unitPackSize")}
            </label>
            <Input
              id="item-unit"
              placeholder={generating ? dots : t("stock.unitPackSizePlaceholder")}
              {...register("unit_pack_size")}
            />
          </div>
        </div>
        {storageTypes.length > 0 && (
          <div>
            <label className="mb-1.5 block text-xs text-gray-400" htmlFor="item-storage-type">
              {t("stock.storageType")}
            </label>
            <PillSelector
              id="item-storage-type"
              options={storageTypes.map((st) => ({
                id: st.id,
                label: storageTypeLabel(t, st) ?? st.display_name,
              }))}
              value={watch("storage_type_id") ?? ""}
              onChange={(v) => setValue("storage_type_id", v)}
            />
          </div>
        )}
        <div>
          <label className="mb-1.5 block text-xs text-gray-400" htmlFor="item-allergens">
            {t("stock.allergens")}
          </label>
          <PillSelector
            id="item-allergens"
            options={EU_14_ALLERGENS.map((a) => ({ id: a, label: allergenLabel(t, a) }))}
            multi
            selected={allergens}
            onToggle={(v) =>
              setAllergens((prev) =>
                prev.includes(v) ? prev.filter((a) => a !== v) : [...prev, v],
              )
            }
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-400" htmlFor="item-category">
            {t("stock.category")}
          </label>
          <Select
            value={selectedCategoryId ?? ""}
            onValueChange={(v) => {
              setValue("category_id", v);
              setValue("subcategory_id", "");
            }}
          >
            <SelectTrigger id="item-category" className="w-full">
              <SelectValue placeholder={t("stock.selectCategory")} />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-400" htmlFor="item-subcategory">
            {t("stock.subcategory")}
          </label>
          <Select
            value={watch("subcategory_id") ?? ""}
            onValueChange={(v) => setValue("subcategory_id", v)}
            disabled={!selectedCategoryId}
          >
            <SelectTrigger id="item-subcategory" className="w-full">
              <SelectValue
                placeholder={
                  selectedCategoryId ? t("stock.selectSubcategory") : t("stock.selectCategoryFirst")
                }
              />
            </SelectTrigger>
            <SelectContent>
              {subcategories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-400" htmlFor="item-aliases">
            {t("stock.aliases")}
          </label>
          <TagInput
            id="item-aliases"
            tags={aliases}
            onTagsChange={setAliases}
            placeholder={t("stock.aliasesPlaceholder")}
          />
          <p className="mt-0.5 text-xs text-gray-400">{t("stock.aliasesHint")}</p>
        </div>
      </Section>

      {/* Pricing */}
      <Section
        title={t("stock.sectionPricing")}
        open={pricingOpen}
        onToggle={() => setPricingOpen(!pricingOpen)}
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-gray-400" htmlFor="item-price">
              {t("stock.price", { currency: itemCurrency })}
            </label>
            <Input
              id="item-price"
              type="text"
              inputMode="decimal"
              placeholder={generating ? dots : undefined}
              {...register("price")}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-400" htmlFor="item-ppkg">
              {t("stock.pricePerKg", { currency: itemCurrency })}
            </label>
            <Input
              id="item-ppkg"
              type="text"
              inputMode="decimal"
              placeholder={
                generating
                  ? dots
                  : autoPricePerKg
                    ? t("stock.pricePerKgAuto", {
                        value: formatCurrency(locale, autoPricePerKg, "EUR"),
                      })
                    : "—"
              }
              {...register("price_per_kg")}
            />
            {autoPricePerKg && (
              <p className="mt-0.5 text-xs text-gray-400">
                {t("stock.pricePerKgCalculated", {
                  value: formatCurrency(locale, autoPricePerKg, "EUR"),
                })}
              </p>
            )}
          </div>
        </div>
      </Section>

      {/* Specifications */}
      <Section
        title={t("stock.sectionSpecifications")}
        open={specsOpen}
        onToggle={() => setSpecsOpen(!specsOpen)}
      >
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="mb-1 block text-xs text-gray-400" htmlFor="item-shelf">
              {t("stock.shelfLife")}
            </label>
            <Input
              id="item-shelf"
              type="text"
              inputMode="decimal"
              placeholder={generating ? dots : undefined}
              {...register("typical_shelf_life_days")}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-400" htmlFor="item-weight">
              {t("stock.weight")}
            </label>
            <Input
              id="item-weight"
              type="text"
              inputMode="decimal"
              placeholder={generating ? dots : undefined}
              {...register("typical_weight_kg")}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-400" htmlFor="item-yield">
              {t("stock.yield")}
            </label>
            <Input
              id="item-yield"
              type="text"
              inputMode="decimal"
              placeholder={generating ? dots : undefined}
              {...register("edible_yield_pct")}
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-400" htmlFor="item-supplier">
            {t("stock.supplier")}
          </label>
          <Input id="item-supplier" {...register("supplier")} />
        </div>
      </Section>

      <div className="flex items-center gap-2">
        {onDelete && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={onDelete}
            disabled={isDeleting}
          >
            <Trash2 className="mr-1.5 size-3.5" />
            {t("stock.delete")}
          </Button>
        )}
        <Button type="submit" className="flex-1" disabled={isPending}>
          {isPending ? t("common.saving") : submitLabel}
        </Button>
      </div>
    </form>
  );
}
